/**
 * Which Web QR instance a participant joined through.
 *
 * The participant lists (GET_Total_PARTICIPANT, ALL_PARTICIPANTS) carry no
 * instance field, so the answer is put together from the per-instance list
 * (WEB_QR_INSTANCE_PARTICIPANTS): every participant an instance returns is
 * indexed by its participation row and, within the instance's campaign, by
 * each user id it is known by. A row that does name its instance
 * directly wins over the index, so the day the API adds the field this keeps
 * working without the extra calls mattering.
 */

import { Observable, forkJoin, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PagesService } from '@services/pages.service';

export interface QrInstanceRef {
  id: string;
  label: string;
}

/** The most WEB_QR_INSTANCE_PARTICIPANTS returns per page; larger values are capped to it. */
const INSTANCE_PARTICIPANTS_PER_PAGE = 500;

/** The instances out of a QR_INSTANCE_LIST answer: `{ contest_id, total, instances }` or a bare array. */
export function readQrInstances(res: any): any[] {
  if (Array.isArray(res?.instances)) return res.instances;
  if (Array.isArray(res)) return res;
  return [];
}

export function qrInstanceLabel(instance: any): string {
  return String(instance?.label || instance?.slug || instance?.id || '').trim();
}

// Expired wins over the is_active flag: the code no longer resolves either way.
export function qrInstanceStatusKey(instance: any): 'expired' | 'active' | 'inactive' {
  if (instance?.expires_at && new Date(instance.expires_at).getTime() < Date.now()) {
    return 'expired';
  }
  return instance?.is_active ? 'active' : 'inactive';
}

function participantsOf(res: any): any[] {
  if (Array.isArray(res?.participants)) return res.participants;
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  if (Array.isArray(res?.data)) return res.data;
  return [];
}

/** Every id a participant row can be matched on. Ids are uuids, so they never collide across tables. */
function participantKeys(row: any): string[] {
  const user = (Array.isArray(row?._end_user) ? row._end_user[0] : row?._end_user) || row?.end_user || {};
  return [
    row?.participant_id, row?.contest_participant_id, row?.end_user_id, row?.user_id,
    user?.id, row?.id,
  ]
    .map((value) => String(value ?? '').trim())
    .filter((value) => !!value);
}

function idOf(value: any): string {
  return String(value ?? '').trim();
}

/** The key of one campaign participation; WEB_QR_INSTANCE_PARTICIPANTS calls it `participant_row_id`. */
function rowKey(id: string): string {
  return `row:${id}`;
}

/**
 * `participant_id` is the app user's id, the same in every campaign they join, so a user key
 * only counts inside the instance's own campaign. Left unscoped, a user who scanned a QR code in
 * one campaign showed that instance on their rows in every other campaign too.
 */
function contestUserKey(contestId: string, userKey: string): string {
  return contestId ? `${contestId}:${userKey}` : userKey;
}

/** Loads each instance's participants and indexes them by participation row and by campaign + user id. */
export function loadQrInstanceIndex(pagesService: PagesService, instances: any[]): Observable<Map<string, QrInstanceRef>> {
  const withId = (instances || []).filter((instance) => instance?.id);
  if (!withId.length) return of(new Map());

  const page = (instance: any, pageNo: number) =>
    pagesService.getAllById('WEB_QR_INSTANCE_PARTICIPANTS', {
      qr_instance_id: instance.id,
      page: pageNo,
      per_page: INSTANCE_PARTICIPANTS_PER_PAGE,
    });

  const calls = withId.map((instance) =>
    page(instance, 1).pipe(
      // The first page says how many there are; the rest are fetched together.
      switchMap((first: any) => {
        const pageTotal = Number(first?.page_total) || 1;
        const contestId = idOf(instance.contest_id ?? first?.instance?.contest_id);
        const rows = (list: any[]) => ({ contestId, participants: list });
        if (pageTotal <= 1) return of(rows(participantsOf(first)));
        const rest = Array.from({ length: pageTotal - 1 }, (_, i) =>
          page(instance, i + 2).pipe(map(participantsOf), catchError(() => of([] as any[]))),
        );
        return forkJoin(rest).pipe(map((pages) => rows(pages.reduce((all, list) => all.concat(list), participantsOf(first)))));
      }),
      map(({ contestId, participants }) => ({ instance, contestId, participants })),
      // One instance failing leaves its participants unlabelled, not the whole column.
      catchError(() => of({ instance, contestId: '', participants: [] as any[] })),
    ),
  );

  return forkJoin(calls).pipe(
    map((results) => {
      const index = new Map<string, QrInstanceRef>();
      const add = (key: string, ref: QrInstanceRef) => { if (!index.has(key)) index.set(key, ref); };
      results.forEach(({ instance, contestId, participants }) => {
        const ref: QrInstanceRef = { id: String(instance.id), label: qrInstanceLabel(instance) };
        participants.forEach((participant: any) => {
          const rowId = idOf(participant?.participant_row_id);
          if (rowId) add(rowKey(rowId), ref);
          participantKeys(participant).forEach((key) => add(contestUserKey(contestId, key), ref));
        });
      });
      return index;
    }),
  );
}

/**
 * The instance a participant row belongs to, or null when it joined some other way.
 * `contestId` is the campaign to read the row in when the row does not say which it is.
 */
export function qrInstanceOfRow(
  row: any,
  index: Map<string, QrInstanceRef>,
  instances: any[] = [],
  contestId = '',
): QrInstanceRef | null {
  const nested = row?.qr_instance && typeof row.qr_instance === 'object' ? row.qr_instance : null;
  const directId = String(nested?.id ?? row?.qr_instance_id ?? row?.instance_id ?? '').trim();
  const directLabel = String(
    nested?.label ?? row?.qr_instance_label ?? (typeof row?.qr_instance === 'string' ? row.qr_instance : '') ?? '',
  ).trim();

  if (directId || directLabel) {
    const known = directId ? instances.find((instance) => String(instance?.id) === directId) : null;
    return { id: directId, label: directLabel || qrInstanceLabel(known) || directId };
  }

  for (const id of [row?.participant_row_id, row?.id]) {
    const ref = id ? index.get(rowKey(idOf(id))) : undefined;
    if (ref) return ref;
  }

  const contest = idOf(row?.contest_id ?? row?.contestId ?? contestId);
  for (const key of participantKeys(row)) {
    // Unscoped keys exist only for instances whose campaign was never known.
    const ref = (contest && index.get(contestUserKey(contest, key))) || index.get(key);
    if (ref) return ref;
  }
  return null;
}
